import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { isNothing } from 'donor-elf-web/lib/object-utils';
import type {
  BulkActionCustomListCustomPropertyOptionRename,
  BulkActionResponseIncludesCustomPropertyInfo,
  BulkActionType,
} from 'donor-elf-web/models/bulk-action';
import ContactsCache, {
  type ContactCustomPropertyInfo,
} from 'donor-elf-web/services/contacts-cache';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { type OptionAndCount } from '.';

interface ComponentArgs {
  propertyInfo: ContactCustomPropertyInfo;
  optionAndCount: OptionAndCount;
  onGoBack: () => void;
  onClose: () => void;
}

let validateNewOption = (oldOption: string, currentOptions: string[]) => {
  return {
    newOption: (_key: string, newValue: string): string | true => {
      if (isNothing(newValue)) {
        return 'Must be present';
      }
      let lowercaseNewLabel = newValue.toLowerCase().trim();
      if (oldOption.toLowerCase() === lowercaseNewLabel) {
        if (oldOption !== newValue) {
          return true;
        } else {
          return 'Have to change the option to something different';
        }
      }
      if (currentOptions.some((p) => p.toLowerCase() === lowercaseNewLabel)) {
        return 'That option already exists';
      }
      return true;
    },
  };
};

export default class ContactDetailsPropertiesCustomPropertyEditOptionsModalRenameComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    let validation = validateNewOption(
      this.args.optionAndCount.option,
      this.args.propertyInfo.o!,
    );
    this.changeset = new Changeset(
      { newOption: '' },
      lookupValidator(validation),
      validation,
    );
  }

  @service declare contactsCache: ContactsCache;

  @tracked changeset?: any;
  @tracked showValidationErrors = false;
  @tracked saving = false;
  @tracked saveError = false;

  bulkActionType: BulkActionType = 'custom_property_list_option_rename';
  @tracked bulkActionCustom?: BulkActionCustomListCustomPropertyOptionRename;

  get textLabel() {
    return `Rename "${this.args.optionAndCount.option}" to:`;
  }

  get renamingMessage() {
    return `Renaming "${this.args.optionAndCount.option}" to "${this.changeset.get('newOption')}"... This will take a few seconds`;
  }

  @action
  async save() {
    await this.changeset.validate();
    if (this.changeset.get('isValid')) {
      if (this.showValidationErrors) {
        this.showValidationErrors = false;
      }
      this.bulkActionCustom = {
        property_label: this.args.propertyInfo.l,
        old_option: this.args.optionAndCount.option,
        new_option: this.changeset.get('newOption'),
      };
      this.saving = true;
    } else {
      this.showValidationErrors = true;
    }
  }

  @action
  async onCompleted(response: BulkActionResponseIncludesCustomPropertyInfo) {
    await this.contactsCache.reloadSpecificContacts(response.contact_ids);
    let updatedCustomPropertyInfo = response.custom_property_info;
    if (updatedCustomPropertyInfo) {
      this.contactsCache.updateCustomPropertyInfo(updatedCustomPropertyInfo);
    }
    this.args.onClose();
  }

  @action
  onError() {
    this.saveError = true;
  }
}
